import * as React from 'react';
import * as ReactQuery from '@tanstack/react-query';
import classNames from 'classnames'
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { TreeSelect } from 'primereact/treeselect';
import { InfoEntry } from '../components/InfoEntry';
import {
  NewWaterMeterDialog,
  EditWaterMeterDialog,
  ChangeWaterMeterBindingDialog,
} from '../components/water-meter';
import {
  listAllCompaniesRequest,
  createWaterMeterRequest,
  listAllWaterMeterRequest,
  bindWaterMeterRequest,
  unbindWaterMeterRequest,
  listAllLatestWaterMeterSensingDataRequest,
} from '../apis/calyxSaaSDashboardAPI';
import { formatISODate } from '../utils/format';

export const vendorWithModelOptions = [
  { label: 'Mingwan Ultrasonic', value: 'MINGWAN_WATER_METER' },
  { label: 'Netvox R718H', value: 'NETVOX_R718H' },
];

export const CrudCalyxWaterMeters = () => {
  const [unitNodeKeySelection, setUnitNodeKeySelection] = React.useState(null);
  const [globalFilter, setGlobalFilter] = React.useState(null);

  const [targetWaterMeter, setTargetWaterMeter] = React.useState(null);
  const [isNewWaterMeterDialogOpen, setIsNewWaterMeterDialogOpen] = React.useState(false);
  const [isEditWaterMeterDialogOpen, setIsEditWaterMeterDialogOpen] = React.useState(false);
  const [isChangeWaterMeterBindingDialogOpen, setIsChangeWaterMeterBindingDialogOpen] = React.useState(false);

  const toast = React.useRef(
    /** @type {Toast | null} */ (null)
  );

  const queryClient = ReactQuery.useQueryClient();

  const allCompaniesQuery = ReactQuery.useQuery(
    ['list', 'companies'],
    async () => await listAllCompaniesRequest(),
  );
  const allWaterMetersQuery = ReactQuery.useQuery(
    ['list', 'water-meters'],
    async () => await listAllWaterMeterRequest(),
  );
  const allLatestWaterMeterSensingDataQuery = ReactQuery.useQuery(
    ['allLatestWaterMeterSensingData'],
    async () => await listAllLatestWaterMeterSensingDataRequest(),
  );

  const createWaterMeterMutation = ReactQuery.useMutation(
    async ({ newWaterMeter }) => await createWaterMeterRequest(newWaterMeter),
    {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: ['list', 'water-meters'] });
        setIsNewWaterMeterDialogOpen(false);
        toast.current.show({
          severity: 'success',
          summary: `Water meter created`,
          life: 3000,
        });
      },
      onError(err) {
        toast.current.show({
          severity: 'error',
          summary: `Cannot create water meter`,
          detail: String(err.response?.data?.message),
          sticky: true,
        });
      },
    }
  );
  const bindWaterMeterMutation = ReactQuery.useMutation(
    async ({ deviceId, houseId }) => await bindWaterMeterRequest(deviceId, houseId),
    {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: ['list', 'water-meters'] });
        setIsChangeWaterMeterBindingDialogOpen(false);
        toast.current.show({
          severity: 'success',
          summary: `Successfully bind water meter`,
          life: 3000,
        });
      },
      onError(err) {
        toast.current.show({
          severity: 'error',
          summary: `Cannot bind water meter`,
          detail: String(err.response?.data?.message),
          sticky: true,
        });
      },
    }
  );
  const unbindWaterMeterMutation = ReactQuery.useMutation(
    async ({ deviceId }) => await unbindWaterMeterRequest(deviceId),
    {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: ['list', 'water-meters'] });
        setIsChangeWaterMeterBindingDialogOpen(false);
        toast.current.show({
          severity: 'success',
          summary: `Successfully unbind water meter`,
          life: 3000,
        });
      },
      onError(err) {
        toast.current.show({
          severity: 'error',
          summary: `Cannot unbind water meter`,
          detail: String(err.response?.data?.message),
          sticky: true,
        });
      },
    }
  );

  const allWaterMeters = React.useMemo(() => {
    if (allCompaniesQuery.data == null) return null;
    if (allWaterMetersQuery.data == null) return null;

    return allWaterMetersQuery.data.map((waterMeter) => {
      const { companyId, businessId, farmId, houseId } = waterMeter;

      const company = allCompaniesQuery.data?.find((company) => company.id === companyId);
      const business = company?.businesses.find((business) => business.id === businessId);
      const farm = business?.farms.find((farm) => farm.id === farmId);
      const house = farm?.houses.find((house) => house.id === houseId);

      return {
        ...waterMeter,
        companyName: company?.name,
        businessName: business?.name,
        farmName: farm?.name,
        houseName: house?.name,
      };
    });
  }, [allCompaniesQuery.data, allWaterMetersQuery.data]);

  const unitTreeOptions = React.useMemo(() => (
    allCompaniesQuery.data?.map((company) => ({
      key: ['company', company.id].join('/'),
      label: `${company.name} (${allWaterMeters?.filter((waterMeter) => waterMeter.companyId === company.id).length ?? 0})`,
      icon: 'pi pi-fw pi-building',
      children: company.businesses.map((business) => ({
        key: ['business', business.id].join('/'),
        label: `${business.name ?? `(${business.type})`} (${allWaterMeters?.filter((waterMeter) => waterMeter.businessId === business.id).length ?? 0})`,
        icon: 'pi pi-fw pi-briefcase',
        children: business.farms.map((farm) => ({
          key: ['farm', farm.id].join('/'),
          label: `${farm.name} (${allWaterMeters?.filter((waterMeter) => waterMeter.farmId === farm.id).length ?? 0})`,
          icon: 'pi pi-fw pi-warehouse',
          children: farm.houses.map((house) => ({
            key: ['house', house.id].join('/'),
            label: `${house.name} (${allWaterMeters?.filter((waterMeter) => waterMeter.houseId === house.id).length ?? 0})`,
            icon: 'pi pi-fw pi-home',
          })),
        })),
      })),
    }))
  ), [allCompaniesQuery.data, allWaterMeters]);

  const selectedUnits = React.useMemo(() => (
    Object.entries(unitNodeKeySelection ?? {})
      .filter(([nodeKey, { checked }]) => checked)
      .map(([nodeKey, { checked }]) => {
        const [unitType, unitId] = nodeKey.split('/');

        return {
          type: unitType,
          id: unitId,
        };
      })
  ), [unitNodeKeySelection]);

  const selectHouseIdsSet = React.useMemo(() => (
    new Set(
      selectedUnits
        .filter((unit) => unit.type === 'house')
        .map((unit) => unit.id)
    )
  ), [selectedUnits]);

  const filteredWaterMeters = React.useMemo(() => {
    if (allWaterMeters == null) return null;

    if (selectedUnits.length === 0) return allWaterMeters;

    return allWaterMeters.filter((scale) => selectHouseIdsSet.has(scale.houseId));
  }, [allWaterMeters, selectHouseIdsSet, selectedUnits.length]);

  const latestWaterMeterSensingDataMap = React.useMemo(
    () => new Map(
      (allLatestWaterMeterSensingDataQuery.data ?? []).map((latestWaterMeterSensingData) => [
        latestWaterMeterSensingData.deviceEUI,
        latestWaterMeterSensingData,
      ])
    ),
    [allLatestWaterMeterSensingDataQuery.data],
  );

  function createWaterMeter() {
    setIsNewWaterMeterDialogOpen(true);
  }
  function editWaterMeter(waterMeter) {
    setTargetWaterMeter(waterMeter);
    setIsEditWaterMeterDialogOpen(true);
  }
  function changeWaterMeterBinding(waterMeter) {
    setTargetWaterMeter(waterMeter);
    setIsChangeWaterMeterBindingDialogOpen(true);
  }

  const dataTableHeaderTemplate = () => (
    <div className='flex flex-column md:flex-row md:justify-content-between md:align-items-center'>
      <h5 className="mb-0">Manage Calyx Water Meters</h5>
      <div className="flex flex-wrap gap-3">
        <TreeSelect
          value={unitNodeKeySelection}
          options={unitTreeOptions}
          className="w-full md:w-24rem"
          selectionMode="checkbox"
          filter
          showClear
          placeholder
          valueTemplate={
            <div className={classNames("flex align-items-center gap-2", { "text-blue-500": selectedUnits.length !== 0 })}>
              <InputIcon className="pi pi-fw pi-filter" />
              <span>{selectedUnits.length === 0 ? 'Filter with unit...' : `Filtered with ${selectHouseIdsSet.size} house(s)`}</span>
            </div>
          }
          onChange={(e) => setUnitNodeKeySelection(e.value)}
        />
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText placeholder="Search ..." onInput={(e) => setGlobalFilter(e.target.value)} />
        </IconField>
      </div>
    </div>
  );

  const serialNumberBodyTemplate = (rowData) => {
    const { companyName, businessName, farmName, houseName } = rowData;

    return (
      <>
        <span className='p-column-title'>Serial Number</span>
        <div className="flex flex-column gap-3">

          {/* Location */}
          <div className="flex flex-wrap align-items-center gap-1 text-sm text-gray-500">
            {[
              companyName,
              businessName,
              farmName,
              houseName,
            ].filter((name) => name != null).map((name, i) => (
              <span className="flex align-items-center white-space-nowrap gap-1" key={i}>
                {i !== 0 ? <i className="pi pi-angle-right" /> : null}
                <span>{name}</span>
              </span>
            ))}
          </div>

          {/* Serial Number */}
          <div>
            <code className="text-lg">{rowData.serialNumber}</code>
          </div>

          {/* Vendor with Model */}
          <div>
            <Tag
              severity="info"
              value={vendorWithModelOptions.find((option) => option.value === rowData.vendorWithModel)?.label ?? rowData.vendorWithModel}
            />
          </div>
        </div>
      </>
    );
  };
  const hardwareSerialBodyTemplate = (rowData) => {
    return (
      <>
        <code>{rowData.hardwareSerial}</code>
      </>
    );
  };
  const statusBodyTemplate = (rowData) => {
    const { hardwareSerial } = rowData;

    const latestWaterMeterSensingData = latestWaterMeterSensingDataMap.get(hardwareSerial);
    const { gatewayEUI, rssi, snr, waterFlow, rawAccumulatedWaterFlow, rawAccumulatedWaterFlowUnit, temperature, instrumentStatus } = latestWaterMeterSensingData ?? {};

    const latestWaterMeterSensingDataTime = latestWaterMeterSensingData != null ? new Date(latestWaterMeterSensingData.loraDate) : null;

    const instrumentProblems = [
      { key: 'isBatteryLow', label: 'Battery Low' },
      { key: 'isPipeBroken', label: 'Pipe Broken' },
      { key: 'isPipeLeakage', label: 'Pipe Leakage' },
      { key: 'isInstrumentStatusError', label: 'Instrument Status Error' },
      { key: 'isReversedPlumbingInstallation', label: 'Reversed Plumbing Installation' },
      { key: 'isTemperatureSensorError', label: 'Temperature Sensor Error' },
      { key: 'isWaterFlowSensorErrorOrEmptyPipe', label: 'Water Flow Sensor Error or Empty Pipe' },
    ].filter(({ key }) => instrumentStatus?.[key] === true);

    return (
      <div className="flex flex-column gap-2">
        <InfoEntry
          severity={
            latestWaterMeterSensingData == null ? 'warning' :
            'info'
          }
          icon="pi pi-clock"
        >
          Latest Sensing Data: {
            latestWaterMeterSensingData != null
              ? formatISODate(latestWaterMeterSensingDataTime)
              : 'No Data'
          }
        </InfoEntry>
        <div className="flex flex-column gap-2 pl-4">
          <InfoEntry
            severity={
              gatewayEUI == null ? 'secondary' :
              'info'
            }
            icon="pi pi-barcode"
          >
            Gateway EUI: {
              gatewayEUI != null
                ? gatewayEUI
                : 'No Data'
            }
          </InfoEntry>
          <InfoEntry
            severity={
              rssi == null ? 'secondary' :
              'info'
            }
            icon="pi pi-wifi"
          >
            <abbr title="Received Signal Strength Indication">RSSI</abbr>: {
              rssi != null
                ? `${rssi} dBm`
                : 'No Data'
            }
          </InfoEntry>
          <InfoEntry
            severity={
              snr == null ? 'secondary' :
              'info'
            }
            icon="pi pi-wifi"
          >
            <abbr title="Signal-to-Noise Ratio">SNR</abbr>: {
              snr != null
                ? `${snr} dB`
                : 'No Data'
            }
          </InfoEntry>
          <InfoEntry
            severity={
              waterFlow == null ? 'secondary' :
              'info'
            }
            icon="pi pi-info-circle"
          >
            Water Flow: {
              waterFlow != null
                ? `${waterFlow.toFixed(0)} L`
                : 'No Data'
            }
          </InfoEntry>
          <InfoEntry
            severity={
              rawAccumulatedWaterFlow == null ? 'secondary' :
              'info'
            }
            icon="pi pi-info-circle"
          >
            Raw Accumulated Water Flow: {
              rawAccumulatedWaterFlow != null
                ? `${rawAccumulatedWaterFlow.toFixed(3)} ${rawAccumulatedWaterFlowUnit?.replace('m3', 'm³')}`
                : 'No Data'
            }
          </InfoEntry>
          <InfoEntry
            severity={
              temperature == null ? 'secondary' :
              'info'
            }
            icon="pi pi-info-circle"
          >
            Temperature: {
              temperature != null
                ? `${temperature.toFixed(1)} °C`
                : 'No Data'
            }
          </InfoEntry>

          {instrumentProblems.length > 0 ? (
            <div className="flex gap-1">
              {
                instrumentProblems.map(({ label }, i) => (
                  <Tag key={i} severity="warning" icon="pi pi-exclamation-triangle" value={label} />
                ))
              }
            </div>
          ) : null}
        </div>
      </div>
    );
  };
  const actionBodyTemplate = (rowData) => {
    return (
      <div className='actions flex gap-2'>
        <Button tooltip='Edit' icon='pi pi-pencil' rounded severity="success" onClick={() => editWaterMeter(rowData)} disabled />
        <Button tooltip='Change Binding' icon='pi pi-link' rounded severity="help" onClick={() => changeWaterMeterBinding(rowData)} />
      </div>
    );
  };

  return (
    <>
      <div className="card">
        <Toast ref={toast} />
        <Toolbar
          start={
            <div className='my-2'>
              <Button label='New' icon='pi pi-plus' severity="success" onClick={createWaterMeter} />
            </div>
          }
          // end={endContent}
          className="mb-4"
        />

        {filteredWaterMeters == null ?
          (
            <DataTable value={Array.from({ length: 10 })} className="p-datatable-striped company-table-skeleton" header={dataTableHeaderTemplate}>
              <Column header='Serial Number' body={<Skeleton />} style={{ width: '300px' }} />
              <Column header='Hardware Serial (EUI)' body={<Skeleton />} style={{ width: '300px' }} />
              <Column header='Status' body={<Skeleton />} style={{ width: '450px' }} />
              <Column header='Action' body={<Skeleton />} style={{ width: '150px' }} />
            </DataTable>
          ) : (
            <DataTable
              header={dataTableHeaderTemplate}
              value={filteredWaterMeters}
              globalFilter={globalFilter}
              globalFilterFields={['serialNumber', 'hardwareSerial', 'companyName', 'businessName', 'farmName']}
              paginator
              rows={50}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            >
              <Column header='Serial Number' body={serialNumberBodyTemplate} field='serialNumber' sortable style={{ width: '300px' }} />
              <Column header='Hardware Serial (EUI)' body={hardwareSerialBodyTemplate} field='hardwareSerial' sortable style={{ width: '300px' }} />
              <Column header='Status' body={statusBodyTemplate} style={{ width: '450px' }} />
              <Column header='Action' body={actionBodyTemplate} style={{ width: '150px' }} />
            </DataTable>
          )}
      </div>

      <NewWaterMeterDialog
        isOpen={isNewWaterMeterDialogOpen}
        onSave={async (newWaterMeter) => await createWaterMeterMutation.mutateAsync({ newWaterMeter })}
        onCancel={() => setIsNewWaterMeterDialogOpen(false)}
      />
      <EditWaterMeterDialog
        isOpen={isEditWaterMeterDialogOpen}
        targetWaterMeter={targetWaterMeter}
        onSave={(editedWaterMeter) => { console.log(editedWaterMeter); }}
        onCancel={() => setIsEditWaterMeterDialogOpen(false)}
      />
      <ChangeWaterMeterBindingDialog
        isOpen={isChangeWaterMeterBindingDialogOpen}
        targetWaterMeter={targetWaterMeter}
        allCompanies={allCompaniesQuery.data}
        onBind={async ({ id: deviceId, houseId }) => await bindWaterMeterMutation.mutateAsync({ deviceId, houseId })}
        onUnbind={async ({ id: deviceId }) => await unbindWaterMeterMutation.mutateAsync({ deviceId })}
        onCancel={() => setIsChangeWaterMeterBindingDialogOpen(false)}
      />
    </>
  );
};

